import React from 'react';
import { Line, Arc, Circle } from 'react-konva';
import DrawingText from './DrawingText';
import { calculateDistance, getDisplayMeasurement } from '../utils/drawingUtils';

// Helper to convert HEX to RGBA for professional transparency effects
const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== 'string') return `rgba(120, 120, 120, ${alpha})`;
  const cleanHex = hex.replace('#', '');
  let r = 0, g = 0, b = 0;
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.slice(0, 2), 16);
    g = parseInt(cleanHex.slice(2, 4), 16);
    b = parseInt(cleanHex.slice(4, 6), 16);
  } else {
    return hex;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * SegmentMeasurementOverlay - AutoCAD-style coordinate projection overlays
 * Renders delta X, delta Y, and angular inclination measurements for any line segment.
 */
const SegmentMeasurementOverlay = ({
  x1,
  y1,
  x2,
  y2,
  activeStrokeColor,
  stageScale = 1,
  textFontSize = 12,
  shapeScaleProps,
  dpi = 72,
  imageSize,
  displayUnitSystem,
  showOverlay = true,
}) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const L = Math.sqrt(dx * dx + dy * dy);

  if (L === 0 || !showOverlay) return null;

  const isDiagonal = Math.abs(dx) > 2 / stageScale && Math.abs(dy) > 2 / stageScale;
  const projLineColor = hexToRgba(activeStrokeColor, 0.4);

  // Safe measurement calculation helper
  const getMeasurementSafe = (val) => {
    return getDisplayMeasurement(
      val,
      shapeScaleProps || { ratio: 1, documentUnit: 'in' },
      dpi,
      imageSize || { width: 100, height: 100 },
      displayUnitSystem || 'auto'
    );
  };

  // Calculate direct segment angle & midpoint
  const angleRad = Math.atan2(dy, dx);
  let angleDeg = angleRad * (180 / Math.PI);
  if (angleDeg > 90) angleDeg -= 180;
  else if (angleDeg < -90) angleDeg += 180;

  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;

  const measurementL = getMeasurementSafe(L);
  const segmentDisplayL = `${measurementL.value} ${measurementL.unit}`;

  // Setup arc rotation and sweep for angle sector
  let arcRotation = 0;
  let arcSweep = 0;
  let bisectorAngle = 0;
  let bisectorRad = 0;
  let textDist = 0;
  let angleTextX = 0;
  let angleTextY = 0;

  if (isDiagonal) {
    const startAngle = dx >= 0 ? 0 : 180;
    const sweepAngle = Math.atan2(dy, dx) * (180 / Math.PI) - startAngle;

    // Normalize sweepAngle to [-180, 180]
    let normalizedSweep = sweepAngle;
    while (normalizedSweep > 180) normalizedSweep -= 360;
    while (normalizedSweep < -180) normalizedSweep += 360;

    if (normalizedSweep >= 0) {
      arcRotation = startAngle;
      arcSweep = normalizedSweep;
    } else {
      arcRotation = startAngle + normalizedSweep;
      arcSweep = -normalizedSweep;
    }

    bisectorAngle = startAngle + normalizedSweep / 2;
    bisectorRad = bisectorAngle * (Math.PI / 180);

    const arcRadius = Math.min(35 / stageScale, L * 0.4);
    textDist = arcRadius + 18 / stageScale;
    angleTextX = x1 + textDist * Math.cos(bisectorRad);
    angleTextY = y1 + textDist * Math.sin(bisectorRad);
  }

  return (
    <>
      {/* 1. Faint original measured line highlight */}
      <Line
        points={[x1, y1, x2, y2]}
        stroke={hexToRgba(activeStrokeColor, 0.35)}
        strokeWidth={1.5 / stageScale}
        dash={[2 / stageScale, 4 / stageScale]}
        perfectDrawEnabled={false}
        listening={false}
      />

      {/* 2. Endpoint Markers */}
      <Circle
        x={x1}
        y={y1}
        radius={3 / stageScale}
        fill={activeStrokeColor}
        stroke="#FFFFFF"
        strokeWidth={1 / stageScale}
        listening={false}
      />
      <Circle
        x={x2}
        y={y2}
        radius={3 / stageScale}
        fill={activeStrokeColor}
        stroke="#FFFFFF"
        strokeWidth={1 / stageScale}
        listening={false}
      />

      {/* 2b. Direct Segment Measurement Label */}
      <DrawingText
        x={midX}
        y={midY}
        text={segmentDisplayL}
        fontSize={textFontSize}
        fill={activeStrokeColor}
        rotation={angleDeg}
        offsetY={-textFontSize * 0.8}
        offsetX={0}
        stageScale={stageScale}
        useBadge={true}
        badgeStroke={activeStrokeColor}
      />

      {/* 3. Coordinate Delta Projections (Δx and Δy) */}
      {isDiagonal && (
        <>
          {/* Delta X horizontal projection */}
          <Line
            points={[x1, y1, x2, y1]}
            stroke={projLineColor}
            strokeWidth={1.2 / stageScale}
            dash={[4 / stageScale, 4 / stageScale]}
            perfectDrawEnabled={false}
            listening={false}
          />
          <DrawingText
            x={(x1 + x2) / 2}
            y={y1 + (dy >= 0 ? -1 : 1) * (14 / stageScale)}
            text={`${getMeasurementSafe(Math.abs(dx)).value} ${getMeasurementSafe(Math.abs(dx)).unit}`}
            fontSize={textFontSize * 0.8}
            fill={activeStrokeColor}
            stageScale={stageScale}
            useBadge={true}
            badgeStroke={activeStrokeColor}
          />

          {/* Delta Y vertical projection */}
          <Line
            points={[x2, y1, x2, y2]}
            stroke={projLineColor}
            strokeWidth={1.2 / stageScale}
            dash={[4 / stageScale, 4 / stageScale]}
            perfectDrawEnabled={false}
            listening={false}
          />
          <DrawingText
            x={x2 + (dx >= 0 ? 1 : -1) * (26 / stageScale)}
            y={(y1 + y2) / 2}
            text={`${getMeasurementSafe(Math.abs(dy)).value} ${getMeasurementSafe(Math.abs(dy)).unit}`}
            fontSize={textFontSize * 0.8}
            fill={activeStrokeColor}
            stageScale={stageScale}
            useBadge={true}
            badgeStroke={activeStrokeColor}
          />
        </>
      )}

      {/* 4. Angle Sector Arc and Label */}
      {isDiagonal && arcSweep > 1.5 && arcSweep < 88.5 && (
        <>
          <Arc
            x={x1}
            y={y1}
            innerRadius={0}
            outerRadius={Math.min(35 / stageScale, L * 0.4)}
            angle={arcSweep}
            rotation={arcRotation}
            fill={hexToRgba(activeStrokeColor, 0.12)}
            stroke={hexToRgba(activeStrokeColor, 0.5)}
            strokeWidth={1 / stageScale}
            listening={false}
            perfectDrawEnabled={false}
          />
          <DrawingText
            x={angleTextX}
            y={angleTextY}
            text={`${arcSweep.toFixed(1)}°`}
            fontSize={textFontSize * 0.75}
            fill={activeStrokeColor}
            stageScale={stageScale}
            useBadge={true}
            badgeStroke={activeStrokeColor}
          />
        </>
      )}
    </>
  );
};

export default SegmentMeasurementOverlay;
